import React, { useContext, useState } from 'react';
import axios from 'axios';
import { showToast } from '../../Components/utils/ShowToast';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { FaEye } from 'react-icons/fa6';
import { IoIosCloseCircleOutline } from 'react-icons/io';
import Dropdown from '../../Components/utils/ReuseDropdown';
import { LoginContext } from "../../Contexts/LoginContext";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { formatDate } from '../../data/constants';
import { FiInfo } from 'react-icons/fi'
import Loader from "../../Components/utils/Loader";
import ConfirmDialog from '../../Components/utils/ReuseConfirmDialog';

function Employees() {

    const { admin } = useContext(LoginContext);

    const [admins, setAdmins] = React.useState([]);
    const [showAddNewModel, setShowAddNewModel] = React.useState(false);
    const [showDetailsModel, setShowDetailsModel] = React.useState(null);
    const [passwordHidden, setPasswordHidden] = React.useState(true);
    const [pageAccess, setPageAccess] = React.useState({});
    const [isEditing, setIsEditing] = React.useState(false);
    const [editAdmin, setEditAdmin] = React.useState(null);
    const [showPassInputs, setShowPassInputs] = React.useState(false);
    const [newEmployee, setNewEmployee] = React.useState({
        email: '',
        name: '',
        newPassword: '',
        confirmNewPass: '',
    });
    const [selectedRole, setSelectedRole] = React.useState('Admin');
    const [isLoading, setIsLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [adminToDelete, setAdminToDelete] = useState(null);

    React.useEffect(() => {
        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        axios
            .get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/all`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            .then((response) => {
                if (response.data.success) setAdmins(response.data.admins);
            }).finally(() => {
                setIsLoading(false);
            })
    }, []);

    const handleOpenAddNewForm = () => {
        setIsEditing(false);
        setNewEmployee({
            email: '',
            name: '',
            password: ''
        });
        setSelectedRole('Admin');
        setPageAccess({});
        setShowAddNewModel(true);
    };

    const handleAddNewEmployee = async (e) => {
        e.preventDefault();
        if (selectedRole === 'Moderator' && Object.keys(pageAccess).length < 1) {
            showToast('Moderator requires at least 1 page access', 'warning');
            return;
        }

        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        const newAdminData = {
            ...newEmployee,
            role: selectedRole,
            access: selectedRole === 'Moderator' ? pageAccess : [],
        };

        try {
            const response = await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/admin/add`, newAdminData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setAdmins([response.data.admin, ...admins]);
                setShowAddNewModel(false);
                showToast('Employee added successfully!', 'success');
            }
        } catch (e) {
            showToast(e.response?.data?.error || 'Something went wrong!', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenEditForm = (adminData) => {
        setEditAdmin(adminData);
        setSelectedRole(adminData.role);
        setPageAccess(adminData.access || {});
        setNewEmployee({
            email: adminData.email,
            name: adminData.name,
            password: ''
        });
        setIsEditing(true);
        setShowAddNewModel(true);
    };

    const handleEditEmployee = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        const updatedAdminData = {
            ...newEmployee,
            role: selectedRole,
            access: selectedRole === 'Moderator' ? pageAccess : undefined,
        };

        try {
            const response = await axios.put(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/admin/update/${editAdmin._id}`,
                updatedAdminData,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                setAdmins((prevAdmins) => [
                    response.data.admin,
                    ...prevAdmins.filter((admin) => admin._id !== editAdmin._id),
                ]);
                setShowAddNewModel(false);
                setIsEditing(false);
                showToast('Employee updated successfully!', 'success');
            }
        } catch (e) {
            showToast(e.response?.data?.error || 'Something went wrong!', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteAdmin = async () => {
        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        try {
            const response = await axios.delete(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/admin/delete/${adminToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setAdmins(admins.filter((admin) => admin._id !== adminToDelete));
                showToast('Employee removed successfully!', 'success');
            }
        } catch (e) {
            showToast(e.response?.data?.error || 'Something went wrong!', 'error');
        } finally {
            setIsLoading(false);
            setDeleteDialogOpen(false);
            setAdminToDelete(null);
        }
    };

    const handleInputChange = (e) => {
        setNewEmployee((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handlePageAccessChange = (page, checked, action) => {
        setPageAccess((prev) => {
            const updatedAccess = { ...prev };

            if (checked) {
                updatedAccess[page] = action;
            } else {
                delete updatedAccess[page];
            }

            return updatedAccess;
        });
    };


    const adminPages = [
        'Dashboard',
        'Categories',
        'Orders',
        'Sellers',
        'Buyers',
        'Products',
        'Revenue',
        'Payments',
        'Social Links',
        'Fee',
        'Terms & Conditions',
        'Disputes',
        'Chats',
    ];

    const editEditorAccess = selectedRole === 'Moderator' && (
        <div className="editEditorAccess">
            {adminPages.map((page, index) => (
                <div key={index} className="accessBtn">
                    <div className="checkboxDiv">
                        <input
                            type="checkbox"
                            className="checkbox"
                            id={`checkbox-${page}`}
                            name={page}
                            checked={!!pageAccess[page]}
                            onChange={(e) => handlePageAccessChange(page, e.target.checked, pageAccess[page] || 'View')}
                        />
                        <label htmlFor={`checkbox-${page}`}>{page}</label>
                    </div>
                </div>
            ))}
        </div>
    );

    const showEditorAccesses = showDetailsModel && showDetailsModel.role === 'Moderator' && (
        <div className="accessTo">
            {Object.entries(showDetailsModel.access)
                .map(([page]) => `${page}`)
                .join(', ')}
        </div>
    );


    const employeesElems = admins.length > 0 ? (
        admins.map((item, index) => (
            <div key={index}>
                <div className="requestRow row">
                    <div className="titleField field">
                        <p className="title">{item.email}</p>
                    </div>
                    <p className="idField field">#{item._id}</p>
                    <p className="nameField field">{item.name}</p>
                    <p className="accessField field">{item.role}</p>
                    <div className="actionsField field">
                        <FaEye className="icon" onClick={() => setShowDetailsModel(item)} />
                        {admin._id !== item._id && (
                            <>
                                <FaEdit className="icon" onClick={() => handleOpenEditForm(item)} />
                                <FaTrash
                                    className="icon"
                                    onClick={() => {
                                        setAdminToDelete(item._id);
                                        setDeleteDialogOpen(true);
                                    }}
                                />
                            </>
                        )}
                    </div>
                </div>
                {admins.length > 1 && admins.length - 1 !== index && <div className="horizontalLine"></div>}
            </div>
        ))
    ) : (
        <div className="row">Nothing to show here...</div>
    );

    const handlePasswordInputChange = (e) => {
        let { name, value } = e.target;
        setNewEmployee({ ...newEmployee, [name]: value });
    }


    if (!(admin && admin?.role === "Super Admin")) return <div>You are not authorized to access this page!</div>


    return (
        <div className="adminEmployeesDiv">
            {isLoading && <Loader type="checkmate" />}
            <div className="adminEmployeesContent">

                <div className="tableDiv">
                    <div className="tableContent">
                        <div className="upper">
                            <h2 className="secondaryHeading">
                                All <span>Employees</span>
                                <span className="totalRows">- {(admins.length < 10 && '0') + admins.length}</span>
                            </h2>
                            <button className="primaryBtn" onClick={handleOpenAddNewForm}>Add New Employee</button>
                        </div>
                        <div className="header">
                            <p className="title">Email</p>
                            <p className="id">Employee ID</p>
                            <p>Employee Name</p>
                            <p>Roles</p>
                            <p>Actions</p>
                        </div>
                        <div className="rows">{employeesElems}</div>
                    </div>
                </div>

            </div>

            {showAddNewModel && (
                <div className="popupDiv addNewModelDiv">
                    <div className="popupContent">
                        <form className="form" onSubmit={isEditing ? handleEditEmployee : handleAddNewEmployee}>
                            <div className="inputDiv">
                                <label>Email <span>*</span></label>
                                <input
                                    type="email"
                                    className="inputField"
                                    name="email"
                                    value={newEmployee.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter Email"
                                    required
                                />
                            </div>
                            <div className="inputDiv">
                                <label>Name <span>*</span></label>
                                <input
                                    type="text"
                                    className="inputField"
                                    name="name"
                                    value={newEmployee.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter Name"
                                    required
                                />
                            </div>
                            <div className="inputDiv">
                                <label>Role</label>
                                <Dropdown
                                    options={['Admin', 'Moderator']}
                                    selected={selectedRole}
                                    onSelect={setSelectedRole}
                                />
                                {editEditorAccess}
                            </div>

                            {!isEditing && <div className="inputDiv">
                                <div className="passwordFieldUpper">
                                    <label htmlFor="password">Create Password <span>*</span></label>
                                    <div
                                        className="hidePasswordBtn"
                                        onClick={() => setPasswordHidden((oldValue) => !oldValue)}
                                    >
                                        <i className={passwordHidden ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'}></i>
                                    </div>
                                </div>
                                <input
                                    type={passwordHidden ? 'password' : 'text'}
                                    className="inputField"
                                    name="password"
                                    value={newEmployee.password}
                                    onChange={handleInputChange}
                                    placeholder="Enter Password"
                                    required={!isEditing}
                                />
                            </div>}

                            {isEditing && <div className="changePassDiv">

                                <div className="upper" onClick={() => setShowPassInputs(prev => !prev)}>
                                    <p>Change Password</p>
                                    {showPassInputs ? <MdKeyboardArrowUp className='icon' /> : <MdKeyboardArrowDown className='icon' />}
                                </div>

                                {showPassInputs && <div className="lower">

                                    <div className="inputDiv">
                                        <div className='inputInnerDiv'>
                                            <input type="password" placeholder='Enter new password' className='inputField' name='newPassword' value={newEmployee.newPassword} onChange={handlePasswordInputChange} required />
                                        </div>
                                        <div className='inputInnerDiv'>
                                            <input type="password" placeholder='Confirm new password' className='inputField' name='confirmNewPass' value={newEmployee.confirmNewPass} onChange={handlePasswordInputChange} required />
                                        </div>
                                    </div>

                                </div>}


                            </div>}

                            <div className="infoSection">
                                <p><FiInfo /> Access to the Employees page is restricted exclusively to you as the Super Admin.</p>
                                <p><FiInfo /> Employees with the "Admin" role have access to all admin panel pages except the Employees page.</p>
                                <p><FiInfo /> Employees with the "Moderator" role are limited to accessing only the pages explicitly assigned by you.</p>
                            </div>

                            <div className="buttonsDiv">
                                <button className="secondaryBtn" type="button" onClick={() => setShowAddNewModel(false)}>
                                    Close
                                </button>
                                <button className="primaryBtn" type="submit" disabled={isLoading}>
                                    {isEditing ? 'Save Changes' : 'Add Employee'}
                                </button>
                            </div>
                        </form>

                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowAddNewModel(false)} />
                    </div>
                </div>
            )}

            {showDetailsModel && (
                <div className="showDetailsModelDiv popupDiv">
                    <div className="popupContent">
                        <div className="form" onSubmit={handleAddNewEmployee}>

                            <h2 className="secondaryHeading">More About <span>Admin</span></h2>

                            <div className="horizontalLine"></div>

                            <div className="row"><p>ID</p><p className='fw500'>#{showDetailsModel._id}</p></div>
                            <div className="row"><p>Email</p><p className='fw500'>{showDetailsModel.email}</p></div>
                            <div className="row"><p>Name</p><p className='fw500'>{showDetailsModel.name}</p></div>
                            <div className="row"><p>Role</p><p className='fw500'>{showDetailsModel.role}</p></div>
                            <div className="row"><p>Admin Since</p><p className='fw500'>{formatDate(showDetailsModel.createdAt)}</p></div>

                            <div className="horizontalLine"></div>

                            {showDetailsModel.role === "Moderator" &&
                                <div>
                                    <p className='fw600'>Access To</p>
                                    <div className='accessTo'>
                                        {showEditorAccesses}
                                    </div>
                                </div>}

                            <div className="buttonsDiv">
                                <button className="secondaryBtn" type="button" onClick={() => setShowDetailsModel(null)}>Close</button>
                            </div>

                        </div>
                    </div>
                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowDetailsModel(null)} />
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={deleteDialogOpen}
                title="Delete Employee"
                message="Are you sure you want to delete this employee? This action cannot be undone."
                onConfirm={handleDeleteAdmin}
                onCancel={() => setDeleteDialogOpen(false)}
                isLoading={isLoading}
            />
        </div>
    );
}

export default Employees;